import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { menuConfig } from '../config/menuConfig';
import { findMenuItemByPath, hasMenuPermission } from '../utils/menuPermissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
  requireAll?: boolean; // true = AND condition, false = OR condition (default)
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredPermissions = [],
  requireAll = false,
  redirectTo = '/login'
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  console.log('🔒 ProtectedRoute Debug:', {
    path: location.pathname,
    isLoading: loading,
    user: user ? { username: user.username } : null,
    userPermissions: user?.allPermissions || [],
    requiredPermissions,
    requireAll
  });

  // Show loading while checking authentication
  if (loading) {
    console.log('⏳ Still loading authentication...');
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    console.log('❌ No user, redirecting to login');
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If specific permissions are provided, check them
  if (requiredPermissions.length > 0) {
    const userPermissions = user.allPermissions || [];
    
    // Check if user is super admin (has all permissions)
    const isSuperAdmin = userPermissions.includes('*') || userPermissions.includes('superadmin');
    
    console.log('🔑 Permission Check:', {
      requiredPermissions,
      userPermissions,
      isSuperAdmin,
      requireAll
    });
    
    if (!isSuperAdmin) {
      // Check permissions based on requirement type
      const hasAccess = requireAll
        ? requiredPermissions.every(permission => userPermissions.includes(permission))
        : requiredPermissions.some(permission => userPermissions.includes(permission));

      console.log('🎯 Access Check Result:', { hasAccess });

      if (!hasAccess) {
        console.log('❌ Access denied - insufficient permissions');
        return (
          <div className="access-denied-container">
            <div className="access-denied-content">
              <h2>🚫 Access Denied</h2>
              <p>You don't have permission to access this page.</p>
              <p>Required permissions: {requiredPermissions.join(', ')}</p>
              <p>Your permissions: {userPermissions.join(', ')}</p>
              <button onClick={() => window.history.back()}>Go Back</button>
            </div>
          </div>
        );
      }
    } else {
      console.log('👑 Super admin access granted');
    }
  } else {
    // If no specific permissions provided, check against menu configuration
    const menuItem = findMenuItemByPath(location.pathname, menuConfig);
    
    console.log('📋 Menu-based permission check:', {
      menuItem: menuItem ? { path: menuItem.path, requiredPermissions: menuItem.requiredPermissions } : null
    });
    
    if (menuItem && menuItem.requiredPermissions.length > 0) {
      const userPermissions = user.allPermissions || [];
      
      if (!hasMenuPermission(menuItem, userPermissions)) {
        console.log('❌ Menu access denied');
        return (
          <div className="access-denied-container">
            <div className="access-denied-content">
              <h2>🚫 Access Denied</h2>
              <p>You don't have permission to access this page.</p>
              <p>Required permissions: {menuItem.requiredPermissions.join(', ')}</p>
              <p>Your permissions: {userPermissions.join(', ')}</p>
              <button onClick={() => window.history.back()}>Go Back</button>
            </div>
          </div>
        );
      }
    }
  }

  console.log('✅ Access granted to:', location.pathname);
  return <>{children}</>;
};

export default ProtectedRoute;
